import { ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { SvgIcon } from '../../components/icons';
import { IconButton } from './Button';
import { OverlayLayer } from './OverlayLayer';

type DialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  width?: 'sm' | 'md' | 'lg';
  /** Footer actions — usually a cancel + a confirm Button. */
  footer?: ReactNode;
  /** When true, ESC and backdrop clicks do not close (destructive flows). */
  strict?: boolean;
  children: ReactNode;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog.
 *
 * The behaviour model follows the production `Dialog`, which is the best
 * accessibility citizen in the repo — focus trap, focus restore, scroll lock,
 * `role="dialog"` + `aria-modal`. Two changes:
 *
 * - `aria-labelledby` points at the rendered title element rather than
 *   duplicating the string into `aria-label`.
 * - ESC closes directly instead of routing through a handler that silently
 *   no-ops under `strict` (a real bug in the production version: pressing ESC
 *   on a strict dialog does nothing at all, with no indication why).
 *
 * Rendered through `OverlayLayer`, which portals into `#root` and re-creates
 * the `.ds-root` token scope around it — without that the dialog renders with
 * a transparent background and native grey buttons.
 */
export const Dialog = ({
  open,
  title,
  onClose,
  width = 'md',
  footer,
  strict = false,
  children,
}: DialogProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !strict) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const items = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, strict],
  );

  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    // Focus the first control rather than the panel, so keyboard users land
    // somewhere useful instead of having to Tab in.
    const timer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current)?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <OverlayLayer>
      <div
        className="ds-dialog-backdrop"
        onMouseDown={(e) => {
          if (!strict && e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={panelRef}
          className="ds-dialog"
          data-ds-width={width}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <div className="ds-dialog-header">
            <h2 id={titleId} className="ds-section-title">
              {title}
            </h2>
            <IconButton label="Close dialog" size="sm" onClick={onClose}>
              <SvgIcon name="close" size={16} />
            </IconButton>
          </div>

          <div className="ds-dialog-body ds-scroll">{children}</div>

          {footer && <div className="ds-dialog-footer">{footer}</div>}
        </div>
      </div>
    </OverlayLayer>
  );
};
